import { useId } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

import { FormDialog } from '@/components/form-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/cn';

// Phase 8.5-05 — quarantine reason form (admin/operator). Backend
// `QuarantineRequestSchema` requires `reason` 1-2000 chars; we mirror
// that with a `.trim().min(1).max(2000)` zod schema and surface a live
// character counter so the operator sees the limit before hitting it.

const REASON_MAX = 2000;

const FormSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'Введите причину карантина')
    .max(REASON_MAX, `Не более ${String(REASON_MAX)} символов`),
});

type FormValues = z.infer<typeof FormSchema>;

const defaultValues: FormValues = { reason: '' };

const ReasonField = () => {
  const id = useId();
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<FormValues>();
  const value = watch('reason');
  const fieldError = errors.reason?.message;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>Причина</Label>
        <span className="text-xs text-muted-foreground">
          {value.length}/{REASON_MAX}
        </span>
      </div>
      <Textarea
        id={id}
        rows={5}
        autoComplete="off"
        spellCheck={false}
        aria-invalid={fieldError ? 'true' : 'false'}
        className={cn(fieldError && 'border-destructive')}
        {...register('reason')}
      />
      {fieldError ? <p className="text-xs text-destructive">{fieldError}</p> : null}
    </div>
  );
};

export interface QuarantineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  submitError?: string;
  onSubmit: (reason: string) => void;
}

export const QuarantineDialog = ({
  open,
  onOpenChange,
  isPending,
  submitError,
  onSubmit,
}: QuarantineDialogProps) => (
  <FormDialog
    open={open}
    onOpenChange={onOpenChange}
    title="Карантин получателя"
    description="Укажите причину — она попадёт в audit log."
    schema={FormSchema}
    defaultValues={defaultValues}
    onSubmit={(values) => {
      onSubmit(values.reason.trim());
    }}
    isPending={isPending}
    submitLabel="Карантин"
    {...(submitError !== undefined ? { submitError } : {})}
  >
    <ReasonField />
  </FormDialog>
);
